import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import MaterialTable from 'material-table';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';

import { ProductNoBarcode, ReportType } from '../../models';
import { PaginatedResponse } from '../../../shared/models';

import { API_URLS } from '../../api/';
import composeRequest from '../../../shared/api/core';
import { checkStatus, parseJSON, handleError } from '../../../shared/api/core/common';
import { getBusinessListFromAPI } from '../../../shared/data/actions';
import { SVG_ICONS } from '../../../shared/icons/SvgIcons';
import { CustomTableTitle, ErrorStatusBar, ExportMenu, ExportStatusBar } from '../../../shared/components';
import { EMPTY_ROW_MSG, ERROR_MSG } from '../../../shared/lib/Localization';
import { initQueryParams, replaceHistory, tableOptions, useQuery } from '../../../shared/lib/TableQueryParams';
import download from 'downloadjs';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
    }),
);

const selectedReportType: ReportType = {
    id: 0,
    name: 'Products without barcode',
    title: 'Products Without Barcode',
    columns: [
        { title: 'Number', field: 'article_number' },
        { title: 'Product', field: 'article_name' },
    ]
};

const getProductsByPage = (
    businessId: string | number, 
    page: number, 
    pageSize: number,
    searchTerm: string | null,
    totalRecs: boolean, 
    orderBy?: string | number
) => {
    let URL = API_URLS.productsNoBarcodesUrl +`?business_ids=${businessId}&page=${page}&page_size=${pageSize}&tot_recs_found=${totalRecs}`;
    searchTerm = searchTerm ? searchTerm.trim() : '';
    if (searchTerm) {
        URL += `&search=${searchTerm}`;
    }
    if(orderBy){
        URL = `${URL}&orderBy=${orderBy}`;
    }
    return composeRequest(URL, 'GET', null);
};

const exportData = (businessId: string | number, searchTerm: string | null,orderBy?: string | number) => {
    let URL = API_URLS.productsNoBarcodesUrl + `export/?business_ids=${businessId}`;
    if (searchTerm) {
        URL += `&search=${searchTerm}`;
    }
    if(orderBy){
        URL = `${URL}&orderBy=${orderBy}`;
    }
    return composeRequest(URL, 'GET', null);
};

const ProductsWithoutBarcodeScreen = (props: any) => {
    const classes = useStyles({});
    const [emptyDataSourceMessage, setEmptyDataSourceMessage] = useState(EMPTY_ROW_MSG);
    const [isErrorStatusVisible, setErrorStatusVisible] = useState(false);
    const tableRef = useRef<{ onQueryChange: any; }>();
    let locationQuery = useQuery(useLocation);
    const [tableQuery, setTableQuery] = useState(initQueryParams(locationQuery));
    const [exportBtnAnchorEl, setExportBtnAnchorEl] = React.useState<null | HTMLElement>(null);
    const [isExporting, setExporting] = useState(false);
    const [initialLoad,setInitialLoad]=useState(true);
    const [totalRecords, setTotalRecords] = useState(null as any)
    const lastQueryKeyRef = useRef<string>('')

    useEffect(() => {
        if (!initialLoad) {
            tableRef && tableRef.current && tableRef.current.onQueryChange();
            setTotalRecords(null)

        }
    }, [props.userData.selectedBusiness]);

    const handleRefresh= () => {
        setTotalRecords(null)
        tableRef.current && tableRef.current.onQueryChange()
    }

    const exportCSV = () => {
        if (props.userData && props.userData.selectedBusiness) {
            setExporting(true);
            const sb = props.userData.selectedBusiness;
            exportData(sb.business_id, tableQuery.search, tableQuery.orderBy)
                .catch(handleError)
                .then(checkStatus)
                .then(res => res.blob())
                .then(data => {
                    download(data, 'Products_Without_Barcode.csv', 'text/csv');
                    setExporting(false);
                    setInitialLoad(false);
                })
                .catch((error: any) => {
                    setExporting(false);
                    if (error) {
                        if (error.status && error.status === 500) {
                            setEmptyDataSourceMessage(ERROR_MSG);
                            setErrorStatusVisible(false);
                        } else if ((error.status === 403 || error.status === 401)) {
                            setEmptyDataSourceMessage(ERROR_MSG);
                            setErrorStatusVisible(true);
                        } else {
                            setEmptyDataSourceMessage(ERROR_MSG);
                            setErrorStatusVisible(false);
                        }
                    }
                });
        }
    };

    const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setExportBtnAnchorEl(event.currentTarget);
    };

    const handleExportMenuClose = (shouldExport: boolean) => {
        if (shouldExport) {
            exportCSV();
        }
        setExportBtnAnchorEl(null);
    };

    return (
        <Container maxWidth={false}>
            <ErrorStatusBar isVisible={isErrorStatusVisible} />
            <ExportStatusBar isVisible={isExporting} />
            <div className="table-wrapper">
                <MaterialTable
                    options={{
                        ...tableOptions(tableQuery),
                        thirdSortClick: false,
                        draggable: false,
                        sorting: true 
                    }}
                    tableRef={tableRef}
                    localization={{
                        body: {
                            emptyDataSourceMessage: emptyDataSourceMessage
                        }
                    }}
                    columns={selectedReportType.columns}
                    data={query =>
                        new Promise((resolve, reject) => {
                            replaceHistory(query, props.history);
                            let pageData = {
                                data: [] as ProductNoBarcode[],
                                totalCount: 0,
                                page: query.page
                            };
                            if (props.userData && props.userData.selectedBusiness) {
                                const sb = props.userData.selectedBusiness;
                                let orderBy = '';
                                if (query.orderBy) {
                                    orderBy = query.orderBy.field as string;
                                    orderBy = `${query.orderDirection && query.orderDirection === 'desc' ? '-' : ''}${orderBy}`;
                                }
                                const queryKey = `${query.search || ''}|${orderBy}|${query.orderDirection || ''}`;
                                const queryChanged = queryKey !== lastQueryKeyRef.current;
                                if (queryChanged) {
                                    lastQueryKeyRef.current = queryKey;
                                    setTotalRecords(null);
                                }
                                const totalRecsAvailable = !queryChanged && totalRecords !== null;
                                const needFreshCount = !totalRecsAvailable;
                                getProductsByPage(
                                    sb.business_id,
                                    query.page + 1, 
                                    query.pageSize, 
                                    query.search, 
                                    totalRecsAvailable,
                                    orderBy
                                )
                                    .catch(handleError) // handle network issues
                                    .then(checkStatus)
                                    .then(parseJSON)
                                    .then((data: PaginatedResponse<ProductNoBarcode>) => {
                                        setErrorStatusVisible(false);
                                        setEmptyDataSourceMessage(EMPTY_ROW_MSG);
                                        const countToUse = needFreshCount ? data.count : totalRecords;
                                        if (needFreshCount && data.count != null) {
                                            setTotalRecords(data.count);
                                        }
                                        pageData = {
                                            data: data.results,
                                            totalCount: countToUse ?? 0,
                                            page: query.page
                                        };
                                        setTableQuery({
                                            ...tableQuery,
                                            orderBy,
                                            totalCount: countToUse ?? 0,
                                            page: query.page,
                                            pageSize: query.pageSize,
                                            search: query.search
                                        });
                                        resolve(pageData);
                                        setInitialLoad(false);
                                    })
                                    .catch((error: any) => {
                                        if (error) {
                                            if (error.status && error.status === 500) {
                                                setEmptyDataSourceMessage(ERROR_MSG);
                                                setErrorStatusVisible(false);
                                            } else if ((error.status === 403 || error.status === 401)) {
                                                setEmptyDataSourceMessage(ERROR_MSG);
                                                setErrorStatusVisible(true);
                                            } else {
                                                setEmptyDataSourceMessage(ERROR_MSG);
                                                setErrorStatusVisible(false);
                                            }
                                        }
                                        setTableQuery({
                                            ...tableQuery,
                                            totalCount: 0,
                                            page: query.page,
                                            pageSize: query.pageSize,
                                            search: query.search
                                        });
                                        resolve(pageData);
                                    });
                            } else {
                                resolve(pageData);
                            }
                        })
                    }
                    title={
                        <CustomTableTitle
                            history={props.history}
                            title={selectedReportType.title}
                            icon={SVG_ICONS.product} 
                            
                            />
                    }
                    actions={[
                        {
                            icon: 'save_alt',
                            tooltip: 'Export',
                            isFreeAction: true,
                            onClick: handleExportClick,
                            disabled: isExporting
                        },
                        {
                            icon: 'refresh',
                            tooltip: 'Refresh Data',
                            isFreeAction: true,
                            onClick: () => handleRefresh(),
                        },
                    ]}
                />
                <ExportMenu
                    anchorEl={exportBtnAnchorEl}
                    handleClose={handleExportMenuClose} />
            </div>
        </Container>
    );
};

const mapStateToProps = (state: any) => {
    return {
        userData: state.userLocalData,
    };
};

export default connect(mapStateToProps, null)(ProductsWithoutBarcodeScreen);